import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../api';

const STATUS_LABELS = {
  pending: { label: 'Pending', color: '#f59e0b' },
  confirmed: { label: 'Confirmed', color: '#10b981' },
  completed: { label: 'Completed', color: '#6b7280' },
  cancelled: { label: 'Cancelled', color: '#ef4444' },
  late_cancelled: { label: 'Late Cancel', color: '#ef4444' },
  no_show: { label: 'No Show', color: '#ef4444' },
};

function fmt(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
}

function fmtTime(t) {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
}

export default function ClientPortal() {
  useParams(); // token reserved for future magic-link flow
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLookup = async (e) => {
    e.preventDefault();
    if (!phone && !email) { setError('Enter your phone number or email.'); return; }
    setError('');
    setLoading(true);
    try {
      const res = await api.post('/clients/lookup', { phone: phone || undefined, email: email || undefined });
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'No account found. Try a different phone or email.');
    } finally {
      setLoading(false);
    }
  };

  const now = new Date();
  const upcoming = data?.appointments?.filter(a => {
    const d = new Date(a.appointment_date + 'T' + a.start_time);
    return d >= now && !['cancelled','late_cancelled','no_show'].includes(a.status);
  }) || [];
  const past = data?.appointments?.filter(a => {
    const d = new Date(a.appointment_date + 'T' + a.start_time);
    return d < now || ['cancelled','late_cancelled','no_show'].includes(a.status);
  }) || [];

  return (
    <div style={{ minHeight: '100vh', background: 'transparent', color: '#fff', fontFamily: "'Inter', -apple-system, sans-serif" }}>
      {/* Header */}
      <div style={{ borderBottom: '1px solid rgba(255,255,255,0.07)', padding: '16px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Link to="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontWeight: 800, fontSize: '20px', letterSpacing: '-0.5px', color: '#fff' }}>mane</span>
        </Link>
        <span style={{ fontSize: '13px', color: '#6b7280' }}>Client Portal</span>
      </div>

      <div style={{ maxWidth: '560px', margin: '0 auto', padding: '48px 24px' }}>
        {!data ? (
          <>
            <div style={{ textAlign: 'center', marginBottom: '40px' }}>
              <div style={{ width: '56px', height: '56px', borderRadius: '16px', background: 'rgba(43,127,255,0.15)', border: '1px solid rgba(43,127,255,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', fontSize: '24px' }}>
                👤
              </div>
              <h1 style={{ fontSize: '26px', fontWeight: 800, margin: '0 0 8px', letterSpacing: '-0.5px' }}>Your Appointments</h1>
              <p style={{ fontSize: '15px', color: '#9ca3af', margin: 0 }}>Enter your phone or email to view your history</p>
            </div>

            <form onSubmit={handleLookup} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#9ca3af', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Phone Number</label>
                <input
                  type="tel"
                  placeholder="(561) 555-0000"
                  value={phone}
                  onChange={e => setPhone(e.target.value)}
                  style={{ width: '100%', padding: '14px 16px', background: '#0B1120', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', color: '#fff', fontSize: '15px', outline: 'none', boxSizing: 'border-box' }}
                />
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.08)' }} />
                <span style={{ fontSize: '12px', color: '#4b5563' }}>or</span>
                <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.08)' }} />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#9ca3af', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Email Address</label>
                <input
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  style={{ width: '100%', padding: '14px 16px', background: '#0B1120', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', color: '#fff', fontSize: '15px', outline: 'none', boxSizing: 'border-box' }}
                />
              </div>

              {error && (
                <div style={{ padding: '12px 16px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: '10px', color: '#fca5a5', fontSize: '14px' }}>
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                style={{ padding: '15px', background: '#2B7FFF', border: 'none', borderRadius: '12px', color: '#fff', fontSize: '15px', fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1, marginTop: '4px' }}
              >
                {loading ? 'Looking up...' : 'View My Appointments'}
              </button>
            </form>

            <p style={{ textAlign: 'center', fontSize: '13px', color: '#4b5563', marginTop: '24px' }}>
              Want to book?{' '}
              <Link to="/book" style={{ color: '#2B7FFF', textDecoration: 'none' }}>Schedule now →</Link>
            </p>
          </>
        ) : (
          <>
            {/* Client header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '32px' }}>
              <div>
                <h2 style={{ margin: '0 0 4px', fontSize: '22px', fontWeight: 800 }}>{data.client.name}</h2>
                <p style={{ margin: 0, fontSize: '13px', color: '#6b7280' }}>{data.client.phone || data.client.email}</p>
              </div>
              <button
                onClick={() => { setData(null); setPhone(''); setEmail(''); }}
                style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#9ca3af', fontSize: '13px', padding: '8px 14px', cursor: 'pointer' }}
              >
                Sign out
              </button>
            </div>

            {/* Upcoming */}
            {upcoming.length > 0 && (
              <section style={{ marginBottom: '32px' }}>
                <h3 style={{ fontSize: '13px', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 12px' }}>Upcoming</h3>
                {upcoming.map(a => <AppointmentCard key={a.id} appt={a} />)}
              </section>
            )}

            {/* Past */}
            {past.length > 0 && (
              <section>
                <h3 style={{ fontSize: '13px', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 12px' }}>Past Appointments</h3>
                {past.map(a => <AppointmentCard key={a.id} appt={a} past />)}
              </section>
            )}

            {upcoming.length === 0 && past.length === 0 && (
              <div style={{ textAlign: 'center', padding: '48px 0', color: '#4b5563' }}>
                <div style={{ fontSize: '32px', marginBottom: '12px' }}>📅</div>
                <p style={{ margin: 0 }}>No appointments yet.</p>
                <Link to="/book" style={{ display: 'inline-block', marginTop: '16px', color: '#2B7FFF', fontSize: '14px', textDecoration: 'none' }}>Book your first appointment →</Link>
              </div>
            )}

            <div style={{ marginTop: '40px', textAlign: 'center' }}>
              <Link to="/book" style={{ display: 'inline-block', padding: '14px 32px', background: '#2B7FFF', borderRadius: '12px', color: '#fff', fontWeight: 700, fontSize: '15px', textDecoration: 'none' }}>
                Book Again
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function AppointmentCard({ appt, past }) {
  const status = STATUS_LABELS[appt.status] || { label: appt.status, color: '#9ca3af' };
  return (
    <div style={{
      padding: '18px 20px',
      background: past ? '#0B1120' : 'rgba(43,127,255,0.05)',
      border: `1px solid ${past ? 'rgba(255,255,255,0.07)' : 'rgba(43,127,255,0.2)'}`,
      borderRadius: '14px',
      marginBottom: '10px',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: '15px', marginBottom: '4px' }}>{appt.service_name || 'Appointment'}</div>
          <div style={{ fontSize: '13px', color: '#9ca3af' }}>
            {fmt(appt.appointment_date)} · {fmtTime(appt.start_time)}
            {appt.duration_minutes ? ` · ${appt.duration_minutes} min` : ''}
          </div>
        </div>
        <span style={{
          fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em',
          color: status.color, background: status.color + '1a', border: `1px solid ${status.color}33`,
          padding: '4px 10px', borderRadius: '20px', whiteSpace: 'nowrap', marginLeft: '12px',
        }}>
          {status.label}
        </span>
      </div>
      {appt.final_price && (
        <div style={{ marginTop: '8px', fontSize: '13px', color: '#6b7280' }}>
          ${parseFloat(appt.final_price).toFixed(0)}
        </div>
      )}
    </div>
  );
}
